import React, { FC, useEffect, useState } from 'react'
import { ReactFC } from 'common/types/ReactFC'
import Link from 'next/link'
import classNames from 'classnames'
import { blogStrings } from 'common/strings/blog-strings'
import Icon from 'components/Icon'
import Input from 'components/forms/Input'
import { safeParseEventValue } from 'common/utils/safeParseEventValue'
import Strings from 'project/localisation'
import useWindowSize from 'components/util/useWindowSize'
import BlogPostMoreResult from './BlogPostMoreResult'
import Button from 'components/forms/Button'
import { Query } from '@prismicio/types'
import { TBlogPostResult } from 'common/cms-types'
import dynamic from 'next/dynamic'

const ModalDefault = dynamic(() => import('components/blog/Modal'), {
  ssr: false,
})

type BlogTagLinkType = {
  tag?: string
  isActive: boolean
}

const BlogTagLink: ReactFC<BlogTagLinkType> = ({ children, isActive, tag }) => {
  return (
    <Link
      className='a-unstyled'
      href={
        tag
          ? `/blog/tag/${encodeURIComponent(
              tag.replace('&', 'And').replace(/ /g, '-').toLowerCase(),
            )}`
          : '/blog'
      }
    >
      <p
        className={classNames(
          'blog-nav__link cursor-pointer',
          'd-xl-inline-block',
          {
            'text-bold': isActive,
          },
        )}
      >
        {children}
      </p>
    </Link>
  )
}

type BlogNavType = {
  posts?: Query<TBlogPostResult> | null
  tags?: string[]
  tag?: string
  search: string
  setSearch: (arg0: string) => void
  onShowMoreClick: () => void
  isLoading: boolean
}

const BlogSearch: FC<BlogNavType> = ({
  isLoading,
  onShowMoreClick,
  posts,
  search,
  setSearch,
  tag,
  tags,
}) => {
  const size = useWindowSize()
  const [termsOpen, setTermsOpen] = useState(false)
  const toggleTerms = () => {
    setTermsOpen(!termsOpen)
  }
  const [blogNavOpen, setBlogNavOpen] = useState(false)
  const toggleBlogNav = () => {
    setBlogNavOpen(!blogNavOpen)

  }
  const [searchOpen, setSearchOpen] = useState(false)
  const toggleSearch = () => {
    setSearchOpen(!searchOpen)
  }

  useEffect(() => {
    if (blogNavOpen) {
      setTimeout(() => {
        document.getElementById('search-input')?.focus()
      }, 500)
    }
  }, [blogNavOpen])

  return (
    <>
      <ModalDefault
        disableScrollWhenModalIsOpen={false}
        search={!search}
        isOpen={termsOpen}
        onDismiss={() => setTermsOpen(false)}
      >
        <div
          className={'d-xl-block d-none bg-white border-bottom border-grey2'}
        >
          <div className={'container px-xxl-27 px-lg-15 px-3-half py-5'}>
            <Input
              iconLeft={'search'}
              paddingLeft={47}
              iconLeftPosition={20}
              paddingRight={44}
              className={'blog-search bg-white border-0'}
              value={search}
              placeholder={Strings.search}
              onChange={(e) => setSearch(safeParseEventValue(e))}
              actionButton={
                <Icon
                  className={'my-5 me-4 cursor-pointer'}
                  name={'close'}
                  width={14}
                  height={14}
                  fill={'black1'}
                />
              }
              onActionButtonClick={() => {
                setSearch('')
                setTermsOpen(false)
              }}
              onEnterPress={() => {
                setTermsOpen(false)
              }}
              isVisibleActionButton
            />
          </div>
        </div>
      </ModalDefault>
      {!!tags && (
        <div
          className={classNames('blog-nav bg-white', {
            'blog-nav--opened': blogNavOpen,
          })}
        >
          <div className='container d-xl-flex justify-content-between gap-4 align-items-center px-xxl-52 px-lg-28 px-5'>
            <div
              onClick={toggleBlogNav}
              className={
                'd-xl-none user-select-none d-flex justify-content-between align-items-center cursor-pointer'
              }
            >
              <a className={'text-bold a-unstyled mb-0'} href='#'>
                {blogStrings.newsroom}
              </a>
              <Icon
                className={'d-block'}
                name={blogNavOpen ? 'chevron-up' : 'chevron-down'}
              />
            </div>
            <div
              className={classNames('d-xl-block mt-2 mt-xl-0 d-xl-block', {
                'd-none': !blogNavOpen,
              })}
            >
              <Input
                id='search-input'

                iconLeft={'search'}
                paddingLeft={20}
                iconLeftPosition={0}
                paddingRight={20}
                className={'blog-search mb-4 d-xl-none'}
                value={search}
                placeholder={blogStrings.searchNewsroom}
                onChange={(e) => setSearch(safeParseEventValue(e))}
                onEnterPress={()=>{
                  setBlogNavOpen(false)
                }}
                onActionButtonClick={() => {
                  setSearch('')
                  setTermsOpen(false)
                }}
              />
              <div
                className={
                  'px-xl-0 px-3-half mt-xl-1 mt-4 d-flex text-nowrap flex-wrap row-gap-1 gap-xl-4 gap-2'
                }
              >
                <BlogTagLink isActive={!tag}>{Strings.showAll}</BlogTagLink>
                {tags.map((v) => (
                  <BlogTagLink
                    key={v}
                    tag={v}
                    isActive={v.toLowerCase().replace('&', 'and') === tag}
                  >
                    {v}
                  </BlogTagLink>
                ))}
              </div>
            </div>
            <Icon
              className={'cursor-pointer d-xl-block d-none'}
              name={'search'}
              onClick={toggleTerms}
            />
          </div>
        </div>
      )}
      {search && (
        <div
          className={classNames(
            'blog-results bg-white container pt-md-26 pb-md-12 py-6 px-xxl-27 px-lg-15 px-3-half',
            {
              'bg-grey6': size.isMdOrLower,
            },
          )}
        >
          <p
            className={classNames(
              'text-muted mb-md-20',
              size.isXlOrHigher ? 'text-small-m' : 'text-xl',
            )}
          >
            {posts ? posts.total_results_size : 0} {blogStrings.resultsFound}
          </p>
          {posts &&
            posts.results.map((post) => {
              return (
                <>
                  <BlogPostMoreResult
                    onLinkClick={() => {
                      setSearch('')
                      setSearchOpen(false)
                    }}
                    key={post.id}
                    post={post}
                  />
                  {!size.isMdOrLower && <hr className={'my-10'} />}
                </>
              )
            })}
          {posts && !!posts.next_page && (
            <div className={'text-center mt-18'}>
              <Button
                theme={'secondary'}
                onClick={onShowMoreClick}
                disabled={isLoading}
              >
                {Strings.showMore}
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default BlogSearch
